import mobileConstants from '../../../mobile/commons/MobileFormsConstants';
import mobileListService from '../../../service/mobile/MobileListService';
import mobileFactoryService from '../../../service/mobile/MobileFactoryService';
import themeService from "ThemeService";

import listService from 'ListService';
import util from 'util/Util';
import imageUtil from 'util/ImageUtil';
import textUtil from 'util/TextUtil'

import * as styleUtils from './styleUtils';
import restService from 'RestService.js';

/**
 * Ensures a given value ends with 'px' if it's purely numeric.
 * Returns the original value otherwise.
 * @param {string|number|null|undefined} value - The value to check.
 * @returns {string|null|undefined} The value with 'px' unit if applicable, or the original value.
 */
export const ensurePxUnit = (value) => {
    if (value === null || value === undefined) return value;
    const strValue = String(value);
    if (/^[0-9]+$/.test(strValue)) {
        return `${strValue}px`;
    }
    return strValue;
};

/**
 * Resolves theme variables and extracts a specific part if the value is comma-separated.
 * @param {string} input - The input string, potentially a theme variable or comma-separated list.
 * @param {number} [index=0] - The index of the part to extract if comma-separated.
 * @returns {string} The resolved value.
 */
export const parseValue = (input, index = 0) => {
    const resolvedValue = themeService.isThemeProperty(input)
        ? themeService.resolve(input)
        : input;

    if (typeof resolvedValue === 'string' && resolvedValue.includes(',')) {
        const parts = resolvedValue.split(',').map(p => p.trim());
        return parts[index];
    }

    return resolvedValue;
};

/**
 * Calculates the final display attributes for a control within the mobile wizard,
 * applying theme styles and respecting manual overrides.
 * @param {object} control - The control object from the React state.
 * @param {object} currentGlobalStyles - The currently active global styles from the wizard state.
 * @param {object} loadedGlobalStyles - The global styles as they were when the wizard was loaded.
 * @returns {object} The final attributes object to be used for rendering the control.
 */
export const generateAttributes = (control, currentGlobalStyles, loadedGlobalStyles) => {
    const savedAttrs = control.attributes;

    let styleCategoryKey = control.factoryKey;
    if (mobileConstants.INPUTS_WITH_ALIGNMENT_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_text_input';
    } else if (mobileConstants.BUTTONS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_button';
    } else if (mobileConstants.TEXTS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_text';
    } else if (mobileConstants.RIGHT_ALIGNED_INPUTS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_numeric_input';
    } else if (mobileConstants.CHECKBOXS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_checkbox';
    } else if (mobileConstants.ADVANCED_INPUTS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_advanced_input';
    }

    const getWizardGlobalsForControl = (styles) => {
        if (!styles) return {};

        //file attachment is a special case because it is a compound control with a button and an input, it gets modified by both categories.
        if (control.factoryKey === 'control_file_attachment') {
            const fontStyling = styles.fontStyling || {};

            const btnStyles = fontStyling['control_button'] || {};
            const buttonGlobals = {
                buttonFontFamily: styles.fontFamily, 
                buttonFontSize: btnStyles.fontSize,
                buttonTextWeight: btnStyles.fontWeight, 
                buttonTextStyle: btnStyles.fontStyle
            };

            const inputStyles = fontStyling['control_advanced_input'] || {};
            const labelGlobals = {
                fontFamily: styles.fontFamily,
                fontSize: inputStyles.fontSize,
                fontWeight: inputStyles.fontWeight,
                fontStyle: inputStyles.fontStyle,
                textAlign: inputStyles.textAlign
            };

            return { ...buttonGlobals, ...labelGlobals };
        }

        const fontStyling = styles.fontStyling || {};
        const base = styleCategoryKey ? fontStyling[styleCategoryKey] || {} : {};
        const specific = fontStyling[control.factoryKey] || {};
        return { ...base, ...specific, fontFamily: styles.fontFamily };
    };

    const currentWizardGlobals = getWizardGlobalsForControl(currentGlobalStyles);

    if (control.isNew) {
        return { ...savedAttrs, ...currentWizardGlobals };
    }

    const loadedWizardGlobals = getWizardGlobalsForControl(loadedGlobalStyles || currentGlobalStyles);

    // Create an "override map" by comparing the control's saved styles to the WIZARD's global styles.
    const customOverrides = {};
    const stylePropsToCheck = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textAlign', 'buttonFontFamily', 'buttonFontSize', 'buttonTextWeight', 'buttonTextStyle'];

    stylePropsToCheck.forEach(key => {
        const savedValue = removePxUnits(savedAttrs[key]?.replace("!important", "").trim());
        const loadedGlobalValue = loadedWizardGlobals[key];

        let areValuesEqual = false;
        if (key === 'fontSize' || key === 'buttonFontSize') {
            areValuesEqual = removePxUnits(savedValue) === removePxUnits(loadedGlobalValue);
        } else {
            areValuesEqual = savedValue === loadedGlobalValue;
        }

        // If the saved value is DIFFERENT from the PREVIOUS global value, it's a override.
        if (savedAttrs.hasOwnProperty(key) && !areValuesEqual) {
            customOverrides[key] = savedValue;
        }
    });

    // Perform the final, definitive merge to calculate the new attributes.
    const finalAttrs = {
        ...savedAttrs,
        ...currentWizardGlobals,
        ...customOverrides
    };

    return finalAttrs;
};

/**
 * Calculates the final display properties for a section within the wizard,
 * applying theme styles and respecting manual overrides.
 * @param {object} section - The section object from the React state.
 * @param {object|null} sectionModel - The legacy Backbone model (optional, used for overrides).
 * @param {object} currentGlobalStyles - The currently active global styles from the wizard state.
 * @param {object} loadedGlobalStyles - The global styles as they were when the wizard was loaded.
 * @returns {object} The final properties object including composite strings (_titleFont, _border).
 */
export const generateSectionProperties = (section, sectionModel, currentGlobalStyles, loadedGlobalStyles) => {
    // Get the complete "saved state"
    const baseProperties = section.attributes || {};
    const mobileOverrideProperties = sectionModel ? styleUtils.getSectionStyleProps(sectionModel) : {};
    const savedProps = { ...baseProperties, ...mobileOverrideProperties };
    const finalProps = { ...savedProps };

    const _loadedGlobalStyles = loadedGlobalStyles || currentGlobalStyles;

    const currentSectionGlobals = currentGlobalStyles.fontStyling['control_section'] || {};
    const currentThemeValues = {
        titleFontFamily: currentGlobalStyles.fontFamily,
        titleFontSize: currentSectionGlobals.fontSize,
        titleTextWeight: currentSectionGlobals.fontWeight,
        titleTextStyle: currentSectionGlobals.fontStyle,
        titleAlign: currentSectionGlobals.textAlign
    };

    const loadedSectionGlobals = _loadedGlobalStyles.fontStyling['control_section'] || {};
    const loadedThemeValues = {
        titleFontFamily: _loadedGlobalStyles.fontFamily,
        titleFontSize: loadedSectionGlobals.fontSize,
        titleTextWeight: loadedSectionGlobals.fontWeight,
        titleTextStyle: loadedSectionGlobals.fontStyle,
        titleAlign: loadedSectionGlobals.textAlign
    };

    for (const key in currentThemeValues) {
        const savedValue = savedProps[key];
        const currentThemeValue = currentThemeValues[key];
        const loadedThemeValue = loadedThemeValues[key];

        let isManualOverride;

        if (key === 'titleFontSize') {
            isManualOverride = ensurePxUnit(savedValue) !== ensurePxUnit(loadedThemeValue);
        } else {
            isManualOverride = savedValue !== loadedThemeValue;
        }

        if (isDefault(savedValue)) {
            isManualOverride = false;
        }

        if (isManualOverride) {
            finalProps[key] = savedValue;
        } else {
            finalProps[key] = currentThemeValue;
        }
    }

    for (const key in finalProps) {
        if (themeService.isThemeProperty(finalProps[key])) {
            finalProps[key] = themeService.resolve(finalProps[key]);
        }
    }

    return finalProps;
};

/**
 * Generates background CSS styles for a page based on its properties.
 * @param {object} page - The page object containing background properties.
 * @returns {object} A CSS style object for the page background.
 */
export const getBackgroundStyles = (page) => {
    const styles = { ...page };
    styles.backgroundImage = 'none';
    styles.backgroundRepeat = 'no-repeat';
    styles.backgroundPosition = 'center';
    styles.backgroundSize = 'contain';

    const imageId = page.backgroundImageAssetId;
    if (imageId && imageId !== 'null') {
        styles.backgroundImage = `url(${restService.getAssetURL(imageId)})`;
        const style = page.backgroundImageStyle;
        if (style === 'stretch') styles.backgroundSize = '100% 100%';
        if (style === 'tile') styles.backgroundRepeat = 'repeat';
    }
    return styles;
};


/**
 * Processes legacy app model attributes into a simpler structure for the wizard.
 * @param {object} appModel - The legacy Backbone model for the application.
 * @returns {object} An attributes object containing app-level styles (background, frame).
 */
export function processAppModelAttributes(appModel) {
    const attributes = {
        backgroundColor: appModel.get('outsideColor') || '#e2e3e6',
        frame: { border: 'none', borderRadius: '0' },
    };
    if (util.isTrue(appModel.get('frame'))) {
        const size = appModel.get('frameSize') || 1;
        const color = appModel.get('frameColor') || '#000000';
        attributes.frame.border = `${size}px solid ${color}`;
        if (util.isTrue(appModel.get('frameRoundCorners'))) {
            attributes.frame.borderRadius = '15px';
        }
    }

    return attributes;
}

/**
 * Processes legacy page model attributes into a simpler structure for the wizard.
 * @param {object} appModel - The legacy Backbone model for the application (used for dimensions).
 * @param {object} pageModel - The legacy Backbone model for the page.
 * @returns {object} An attributes object containing page-level styles and properties.
 */
export function processPageModelAttributes(appModel, pageModel) {
    const attributes = {
        backgroundColor: themeService.resolve(pageModel.get('backgroundColor')) || '#ffffff',
        backgroundImageAssetId: themeService.resolve(pageModel.get('backgroundImageAssetId')),
        backgroundImageStyle: themeService.resolve(pageModel.get('backgroundImageStyle')),
        width: textUtil.convertFromPixelToUnit(appModel.get('formWidth') || 800, appModel.get('dpi'), appModel.get('unit')),
        height: textUtil.convertFromPixelToUnit(appModel.get('formHeight') || 600, appModel.get('dpi'), appModel.get('unit')),
    }

    return attributes;
}

export const initializeControlForLayout = (control, defaultMargins) => {
    const initializedControl = { ...control };

    initializedControl.isNew = true;
    initializedControl.isHidden = false; 

    initializedControl.margins = { ...defaultMargins };
    
   if (Array.isArray(initializedControl.children) && initializedControl.children.length > 0) {

    let sortedChildren = [...initializedControl.children];
    sortedChildren.sort((a, b) => {
        const indexA = parseInt(a.tabIndex || 0, 10);
        const indexB = parseInt(b.tabIndex || 0, 10);
        return indexA - indexB;
    });

        initializedControl.children = sortedChildren.map(child => 
            initializeControlForLayout(child, defaultMargins)
        );
    }

    return initializedControl;
};

/**
 * Processes a legacy desktop control model (Backbone) into a standardized
 * JavaScript object suitable for use in the React Mobile Wizard.
 * Merges desktop styles with mobile-specific overrides if available.
 * @param {object} model - The legacy Backbone model of the desktop control.
 * @param {string} [context='mobile'] - The context ('mobile' to check for mobile overrides).
 * @returns {object} A standardized control object for the wizard.
 */
export function processControlModel(model, context = 'mobile') {

    const originalId = model.get("id");
    const mobileObject = listService.getMobileObject(originalId);
    let mobileModel = null;
    if (context === 'mobile') {
        const mobileControlView = mobileListService.getMobileControl(originalId);
        mobileModel = mobileControlView ? mobileControlView.model : null;
    }

    const controlData = {
        id: originalId,
        isNew: mobileObject ? mobileObject.isNew : false,
        isPlaced: mobileObject ? !mobileObject.isHidden : false,
        factoryKey: model.get("factoryKey"),
        name: model.get('name'),
        top: model.get("top"),
        left: model.get("left"),
        width: ensurePxUnit(model.get("width")),
        height: ensurePxUnit(model.get("height")),
        isDraggable: true,
        zOrder: model.get('zOrder'),
        tabIndex: model.get("tabIndex"),
        attributes: {}
    };

    switch (controlData.factoryKey) {
        case 'control_text':
        case 'control_small_text':
        case 'control_large_heading':
        case 'control_medium_heading':
        case 'control_small_heading':
        case 'control_bullet_text':
        case 'control_link':
            controlData.text = model.get('text');
            controlData.isHtml = model.get('isHtml') === true || model.get('isHtml') === 'true';
            controlData.attributes = mergeMobileStyles(styleUtils.getTextStyleProps, model, mobileModel);
            if (controlData.factoryKey === 'control_bullet_text') {
                controlData.items = model.get('text')?.split('\r').filter(item => item) || [];
            }
            break;

        case 'control_button':
        case 'control_submit_button':
        case 'control_page_button':
        case 'control_print_button':
        case 'control_play_form_button':
        case 'control_play_report_button':
            controlData.text = model.get('text');
            controlData.attributes = mergeMobileStyles(styleUtils.getButtonStyleProps, model, mobileModel);
            break;

        case 'control_text_input':
        case 'control_numeric_input':
        case 'control_password_input':
        case 'control_multiple_text_input':
            controlData.label = model.get('name');
            controlData.defaultValue = model.get('text');
            controlData.placeholder = model.get('caption');
            controlData.inputType = controlData.factoryKey === 'control_password_input' ? 'password' : 'text';
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);
            break;

        case 'control_date_input':
            controlData.label = model.get('name');
            controlData.defaultValue = model.get('specificDisplay');
            controlData.placeholder = model.get('format');
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);
            break;

        case 'control_date_calendar':
            controlData.isDraggable = false;
            controlData.showNow = util.isTrue(model.get('showNow'));
            controlData.specificDate = model.get('specific');
            controlData.attributes = mergeMobileStyles(styleUtils.getCalendarStyleProps, model, mobileModel);
            controlData.unsupportedMessage = 'Date Calendar is not supported in mobile design.';
            break;

        case 'control_time_input':
            controlData.label = model.get('name');
            controlData.hasSeconds = util.isTrue(model.get('hasSeconds'));
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);

            const showNow = util.isTrue(model.get('showNow'));
            const showEmpty = util.isTrue(model.get('showEmpty'));
            const is24Hour = util.isTrue(model.get('is24HourClock'));

            if(showNow){
                controlData.defaultValue = formatTime(new Date(), is24Hour, controlData.hasSeconds);
            }else{
                if(showEmpty){
                    controlData.defaultValue = '';
                }
                else{
                    const specificDate = new Date(model.get('specific'));
                    controlData.defaultValue = formatTime(specificDate, is24Hour, controlData.hasSeconds);
                }
            }

            break;

        case 'control_checkbox':
        case 'control_radio_button':
            controlData.label = model.get('text');
            controlData.isChecked = util.isTrue(model.get('selected'));
            controlData.attributes = mergeMobileStyles(styleUtils.getSelectionControlStyleProps, model, mobileModel);
            break;

        case 'control_checkbox_group':
        case 'control_radio_button_group':
        case 'control_group':
            controlData.attributes = mergeMobileStyles(styleUtils.getGroupStyleProps, model, mobileModel);
            controlData.hasTitle = util.isTrue(model.get('hasTitle'));
            controlData.titleText = model.get('titleText');

            const childList = model.get('childList');

            if (childList && childList.length > 0) {
                const isAllCheckboxes = childList.every(c => c.get('factoryKey') === 'control_checkbox');
                const isAllRadios = childList.every(c => c.get('factoryKey') === 'control_radio_button');

                if (isAllCheckboxes) {
                    controlData.factoryKey = 'control_checkbox_group';
                } else if (isAllRadios) {
                    controlData.factoryKey = 'control_radio_button_group';
                }
            } else {
                controlData.isDraggable = false;
                controlData.unsupportedMessage = 'Empty groups are not supported in mobile design.';
            }

            // Recursively process all children so they can be displayed
            controlData.children = model.get('childList')?.map(childModel =>
                processControlModel(childModel)
            ) || [];

            if (controlData.factoryKey === 'control_group') {
                controlData.isDraggable = false;
                controlData.unsupportedMessage = 'General object groups are not supported in mobile design.';
            }
            break;

        case 'control_drop_down':
            controlData.label = model.get('name');
            const gridData = model.get('gridData') || [];
            const selectedItem = gridData.find(item => item.selected === 'selected');
            controlData.displayValue = selectedItem ? selectedItem.caption : '';
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);
            break;

        case 'control_mask_text_input':
            controlData.label = model.get('name');
            controlData.defaultValue = model.get('textValue');
            controlData.placeholder = model.get('mask');
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);
            break;

        case 'control_numeric_stepper':
            controlData.label = model.get('name');
            controlData.defaultValue = model.get('value');
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);
            break;

        case 'control_horizontal_separator':
        case 'control_vertical_separator':
            controlData.attributes = mergeMobileStyles(styleUtils.getSeparatorsStyleProps, model, mobileModel);
            if (controlData.factoryKey === 'control_vertical_separator') {
                controlData.isDraggable = false;
                controlData.unsupportedMessage = `${model.get('name')} is not supported in mobile.`;
            }
            break;

        case 'control_websignature':
            controlData.label = model.get('name');
            controlData.saveLabel = model.get('buttonLabelSave');
            controlData.clearLabel = model.get('buttonLabelClear');
            controlData.buttonPosition = model.get('buttonPosition');
            controlData.attributes = mergeMobileStyles(styleUtils.getWebSignatureButtonStyleProps, model, mobileModel);
            controlData.attributes.padBackgroundColor = model.get('imageBackgroundColor');
            controlData.attributes.inkColor = model.get('signatureColor');
            break;

        case 'control_horizontal_slider':
        case 'control_vertical_slider':
            controlData.label = model.get('name');
            controlData.value = model.get('value');
            controlData.minValue = model.get('minValue');
            controlData.maxValue = model.get('maxValue');
            controlData.minText = model.get('minText');
            controlData.maxText = model.get('maxText');
            controlData.attributes = mergeMobileStyles(styleUtils.getSliderStyleProps, model, mobileModel);
            if (controlData.factoryKey === 'control_vertical_slider') {
                controlData.isDraggable = false;
                controlData.unsupportedMessage = 'Vertical Slider is not supported in mobile design.';
            }
            break;

        case 'control_table':
            controlData.label = model.get('name');
            controlData.showHeader = util.isTrue(model.get('showHeader'));
            const tableGridData = model.get('gridData') || [];
            controlData.columns = tableGridData.map(col => ({
                header: col.get("header"),
                width: col.get("width"),
            }));
            controlData.attributes = mergeMobileStyles(styleUtils.getTableStyleProps, model, mobileModel);
            controlData.rowHeight = model.get('rowHeight');
            controlData.headerHeight = ensurePxUnit(model.get('headerHeight'));
            controlData.buttons = model.get('buttons');
            break;

        case 'control_ranking':
            controlData.isDraggable = false;
            controlData.label = model.get('name');
            const rankingGridData = model.get('gridData') || [];
            controlData.items = rankingGridData.map(d => d.item);
            controlData.attributes = mergeMobileStyles(styleUtils.getRankingStyleProps, model, mobileModel);
            break;

        case 'control_list':
            controlData.label = model.get('name');
            const listGridData = model.get('gridData') || [];
            controlData.items = listGridData.map(item => ({
                value: item.value,
                caption: item.caption,
                isSelected: item.selected === 'selected',
            }));
            controlData.attributes = mergeMobileStyles(styleUtils.getInputStyleProps, model, mobileModel);
            break;

        case 'control_picture':
            controlData.label = model.get('name');
            controlData.assetId = model.get('assetId');
            controlData.stretch = util.isTrue(model.get('stretch'));
            controlData.maintainAspectRatio = util.isTrue(model.get('maintainAspectRatio'));
            controlData.attributes = mergeMobileStyles(styleUtils.getImageStyleProps, model, mobileModel);
            break;

        case 'control_progress_bar':
            controlData.label = model.get('name');
            controlData.value = parseFloat(model.get('value') || 0);
            controlData.maxValue = parseFloat(model.get('maxValue') || 100);
            controlData.showLabel = util.isTrue(model.get('showLabel'));
            controlData.labelText = model.get('label') || '%1%';
            controlData.attributes = mergeMobileStyles(styleUtils.getProgressBarStyleProps, model, mobileModel);
            break;

        case 'control_captcha':
            controlData.label = model.get('name');
            controlData.attributes = {};
            controlData.imageUrl = imageUtil.getCaptchaPresentationServerURLByView({ model });
            break;

        case 'control_file_attachment':
            controlData.label = model.get('name');
            controlData.buttonLabel = model.get('buttonLabelAttach');
            controlData.statusText = 'Status';
            controlData.buttonLeft = util.isTrue(model.get('buttonPositionLeft'));
            controlData.attributes = mergeMobileStyles(styleUtils.getFileAttachmentStyleProps, model, mobileModel);
           
            break;
    }
    return controlData;
}

/**
 * Creates a default section object structure when a new section is added in the wizard.
 * Uses the legacy factory service to get default model attributes.
 * @param {string} id - The unique ID for the new section.
 * @param {string} name - The initial name for the new section.
 * @param {string} pageId - The ID of the page the section belongs to.
 * @param {string} columnId - The ID of the column the section belongs to.
 * @param {number} index - The order index of the section.
 * @returns {object} A new section object with default attributes.
 */
export const createDefaultSectionObject = (id, name, pageId, columnId, index) => {
    // Use the legacy factory to create a temporary model.
    const factory = mobileFactoryService.factories[mobileConstants.SECTION_FACTORY_KEY].factory
    const tempView = factory.createView({ id, name });
    const modelDefaults = tempView.model.toJSON();

    // Translate the model attributes into nested JS object structure
    const sectionObject = {
        id,
        name,
        pageId,
        columnId,
        index,
        controls: [],
        isNew: true,
        attributes: {
            hasTitle: util.isTrue(modelDefaults.hasTitle),
            titleText: modelDefaults.titleText,
            titleAlign: null,
            titleHeight: modelDefaults.titleHeight,
            titleColor: modelDefaults.titleColor,
            titleFontFamily: modelDefaults.titleFontFamily,
            titleFontSize: modelDefaults.titleFontSize,
            titleTextWeight: modelDefaults.titleTextWeight,
            titleTextStyle: modelDefaults.titleTextStyle,
            titleBoldSelection: modelDefaults.titleBoldSelection || "",
            titleItalicSelection: modelDefaults.titleItalicSelection || "",
            titleBackgroundColor: modelDefaults.headerBackgroundColor,
            backgroundColor: modelDefaults.backgroundColor,
            borderColor: modelDefaults.borderColor,
            borderSize: modelDefaults.borderSize,
            borderStyle: modelDefaults.borderStyle,
            cornerRadius: modelDefaults.cornerRadius,
            transparentBackground: modelDefaults.transparentBackground,
        }
    };

    return sectionObject;
};

/**
 * Updates the legacy ThemeService with the current section font styles from the wizard.
 * @param {object} newGlobalStyles - The current global styles object from the wizard state.
 */
export const syncGlobalStylesToThemeService = (newGlobalStyles) => {
    const sectionStyles = newGlobalStyles.fontStyling?.control_section;
    if (!sectionStyles) return;

    const themeKey = '~sectionFont';
    const currentThemeParts = themeService.normalizedThemeArray(themeKey);
    const newThemeParts = [
        newGlobalStyles.fontFamily || currentThemeParts[0],
        sectionStyles.fontSize ? String(sectionStyles.fontSize).replace('px', '') : currentThemeParts[1],
        sectionStyles.fontWeight || currentThemeParts[2],
        sectionStyles.fontStyle || currentThemeParts[3]
    ];

    const newThemeValue = newThemeParts.join(',');

    themeService.updateItem(newThemeValue, themeKey);
};


/**
 * Parses margin values from either a saved '_margins' string or a 'margins' object.
 * Provides default margins if parsing fails or input is invalid.
 * @param {object} propertiesOrAttributes - An object containing margin data (e.g., _margins: "0,5,10,5" or margins: {top:"0", ...}).
 * @returns {{top: string, right: string, bottom: string, left: string}} An object with top, right, bottom, left margin values.
 */
export const parseSavedMargins = (propertiesOrAttributes) => {
    const defaultMargins = { top: "0", right: "5", bottom: "10", left: "5" };
    let marginSource = propertiesOrAttributes?._margins;

    if (marginSource === undefined && propertiesOrAttributes?.margins) {
        const marginsObj = propertiesOrAttributes.margins;
        if (typeof marginsObj === 'object' && marginsObj !== null) {
            return {
                top: String(marginsObj.top ?? defaultMargins.top),
                right: String(marginsObj.right ?? defaultMargins.right),
                bottom: String(marginsObj.bottom ?? defaultMargins.bottom),
                left: String(marginsObj.left ?? defaultMargins.left),
            };
        }
        marginSource = propertiesOrAttributes.margins;
    }

    if (typeof marginSource === 'string') {
        const parts = marginSource.split(",");
        if (parts.length === 4) {
            return { top: parts[0].trim(), right: parts[1].trim(), bottom: parts[2].trim(), left: parts[3].trim() };
        }
    }
    return defaultMargins;
};

/**
 * Calculates the expected theme attributes for a control based on global styles.
 * Used during the save process to compare against initial attributes for override detection.
 * @param {object} control - The control object (needs id and factoryKey).
 * @param {object} globalStyles - The current global styles object.
 * @returns {object} An attributes object representing the theme styles for this control.
 */
export const calculateWizardThemeAttributes = (control, globalStyles) => {
    let styleCategoryKey = control.factoryKey;
    if (mobileConstants.INPUTS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_text_input';
    } else if (mobileConstants.BUTTONS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_button';
    } else if (mobileConstants.TEXTS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_text';
    } else if (mobileConstants.RIGHT_ALIGNED_INPUTS_FACTORY_KEYS.includes(control.factoryKey)) {
        styleCategoryKey = 'control_numeric_input';
    }

    const fontStyling = globalStyles.fontStyling || {};
    const globalFontStylesForCategory = fontStyling[styleCategoryKey] || {};

    const desktopModel = listService.getObject(control.id)?.model;
    let baseDesktopAttributes = {};
    if (desktopModel) {
        const styleGetter = getStylePropsGetter(control.factoryKey);
        if (styleGetter) {
            const rawDesktopAttrs = styleGetter(desktopModel);
            Object.keys(rawDesktopAttrs).forEach(key => {
                if (typeof rawDesktopAttrs[key] === 'object' && rawDesktopAttrs[key] !== null) {
                    baseDesktopAttributes[key] = rawDesktopAttrs[key];
                } else {
                    baseDesktopAttributes[key] = themeService.resolve(rawDesktopAttrs[key]);
                }
            });
        }
    }

    const themeAttributes = {
        ...baseDesktopAttributes,
        fontFamily: globalStyles.fontFamily || baseDesktopAttributes.fontFamily || 'Poppins',
        fontSize: globalFontStylesForCategory.fontSize || baseDesktopAttributes.fontSize,
        fontWeight: globalFontStylesForCategory.fontWeight || baseDesktopAttributes.fontWeight,
        fontStyle: globalFontStylesForCategory.fontStyle || baseDesktopAttributes.fontStyle,
        textAlign: globalFontStylesForCategory.textAlign || baseDesktopAttributes.textAlign,
    };

    Object.keys(themeAttributes).forEach(key => {
        if (themeAttributes[key] === undefined || themeAttributes[key] === null) {

            delete themeAttributes[key];
        }
    });


    return themeAttributes;
};


/**
 * Parses margin values specifically from a wizard 'attributes' object.
 * Handles both object format (margins: {top: ...}) and legacy string format (_margins: "...").
 * @param {object} attributes - The attributes object from the wizard state or loaded data.
 * @returns {{top: string, right: string, bottom: string, left: string}} An object with top, right, bottom, left margin values.
 */
export const getMarginsFromAttributes = (attributes) => {
    const defaultMargins = { top: "0", right: "5", bottom: "10", left: "5" };
    if (attributes?.margins && typeof attributes.margins === 'object') {
        return {
            top: String(attributes.margins.top ?? defaultMargins.top),
            right: String(attributes.margins.right ?? defaultMargins.right),
            bottom: String(attributes.margins.bottom ?? defaultMargins.bottom),
            left: String(attributes.margins.left ?? defaultMargins.left),
        };
    }
    
    if (attributes?._margins && typeof attributes._margins === 'string') {
        const parts = String(attributes._margins).split(",");
        if (parts.length === 4) {
            return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
        }
    }
    return defaultMargins;
};

/**
 * Normalizes an attributes object loaded from legacy storage or XML.
 * Ensures colors have '#', converts boolean strings, adds 'px' units,
 * applies default font properties, and standardizes margins to an object format.
 * @param {object} inputAttrs - The raw attributes object.
 * @returns {object} A normalized attributes object suitable for the wizard state.
 */
export const normalizeAttributes = (mobileData) => {
    const attributes = { ...(mobileData.attributes || {}) };
   
    // Ensure Colors have '#'
    for (const key in attributes) {
        if (key.toLowerCase().includes('color') && attributes[key]) {
            attributes[key] = formatColorForLoad(attributes[key]);
        }
        // Convert boolean strings
        if (attributes[key] === 'true') attributes[key] = true;
        if (attributes[key] === 'false') attributes[key] = false;
    }

    // Ensure px units for specific properties
    const unitProps = ['fontSize', 'buttonFontSize', 'borderSize', 'cornerRadius', 'width', 'height', 'top', 'left'];
  
    unitProps.forEach(key => {
        if (attributes.hasOwnProperty(key)) {
            attributes[key] = ensurePxUnit(attributes[key]);
        }
    });

    //If font properties come from single font property
    if(attributes.font){
        const fonts = attributes.font.split(",");
        attributes.fontFamily = fonts[0] || null;
        attributes.fontSize   = fonts[1] || null;
        attributes.fontWeight = fonts[2] || null;
        attributes.fontStyle  = fonts[3] || null;
    }

    if(attributes.buttonFont){
        const btnFonts = attributes.buttonFont.split(",");
        attributes.buttonFontFamily = btnFonts[0] || null;
        attributes.buttonFontSize   = btnFonts[1] || null;
        attributes.buttonTextWeight = btnFonts[2] || null;
        attributes.buttonTextStyle  = btnFonts[3] || null;
    }

    // Apply Defaults for essential missing font properties
    if (!attributes.fontFamily) attributes.fontFamily = 'Poppins';
    if (!attributes.fontSize) attributes.fontSize = '12px';
    if (!attributes.fontWeight) attributes.fontWeight = 'normal';
    if (!attributes.fontStyle) attributes.fontStyle = 'normal';

    if(mobileData.factoryKey === "control_file_attachment"){
        if (!attributes.buttonFontFamily) attributes.buttonFontFamily = 'Poppins';
        if (!attributes.buttonFontSize) attributes.buttonFontSize = '12px';
        if (!attributes.buttonTextWeight) attributes.buttonTextWeight = 'normal';
        if (!attributes.buttonTextStyle) attributes.buttonTextStyle = 'normal';
    }

    // Remove any properties that ended up undefined after ensuring units
     Object.keys(attributes).forEach(key => {
        if (attributes[key] === undefined) {
             delete attributes[key];
        }
    });


    return attributes;
};

/**
 * Calculates the expected margins based on the global margin spacing set
 * @param {object} savedMargins - The control object margins
 * @param {object} globalStyles - The current global styles object.
 * @returns {object} An object representing the margins
 */
export const syncMarginsWithGlobalSpacing = (savedMargins, currentMargin, currentSpacingValue) => {
    const UNSET_VALUE = "5"; // The "Zero" or "Off" state
    const activeValue = parseInt(currentSpacingValue);

    // Default to all "Off"
    const margins = { 
        top: UNSET_VALUE, 
        right: UNSET_VALUE, 
        bottom: UNSET_VALUE, 
        left: UNSET_VALUE 
    };

    if (!savedMargins) return margins;

    //Handle String Input
    if (typeof currentMargin === 'string') {
        const type = currentMargin.toLowerCase();
        
        if (type === 'top-bottom') {
            margins.top = activeValue;
            margins.bottom = activeValue;
        }
        else if (type === 'bottom') {
            margins.bottom = activeValue;
        } else if (type === 'top') {
            margins.top = activeValue;
        }
        return margins;
    }

    // Fail Safe
    ['top', 'right', 'bottom', 'left'].forEach(side => {
        const savedVal = savedMargins[side] || UNSET_VALUE;
        if (savedVal !== UNSET_VALUE) {
            margins[side] = activeValue;
        }
    });

    return margins;
};


/**
 * Converts legacy saved properties (with '_' prefix, composite strings like _font, _border)
 * into a normalized 'attributes' object suitable for the wizard state.
 * @param {object} properties - The legacy properties object (e.g., from XML Properties tag).
 * @returns {object} A normalized attributes object.
 */
export const convertSavedPropertiesToAttributes = (properties) => {
    const attributes = {};
    if (!properties) return attributes;

    const {
        _font, _buttonFont, _headerFont, _titleFont,
        _border, 
         ...restProps
    } = properties;

    for (const key in restProps) {
        if (!restProps.hasOwnProperty(key) || !key.startsWith('_')) continue;

        const value = restProps[key];
        const attrKey = key.substring(1);

        if (attrKey.toLowerCase().includes('color')) {
            attributes[attrKey] = formatColorForLoad(value);
        } else if (attrKey === 'margins' && typeof value === 'string') {
            const parts = value.split(',');
            if (parts.length === 4) {
                attributes[attrKey] = { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
            } else {
                attributes[attrKey] = { top: "0", right: "5", bottom: "10", left: "5" };
            }
        }
        else if (value === 'true') {
             attributes[attrKey] = true;
        } else if (value === 'false') {
             attributes[attrKey] = false;
        }
        //Don't convert numbers to Number, keep as string/value to be unitized later
        else if (['width', 'height', 'top', 'left', 'zOrder', 'fontSize', 'borderSize', 'cornerRadius'].includes(attrKey) && !isNaN(value)) {
            attributes[attrKey] = Number(value);
        }
        else {
            attributes[attrKey] = value;
        }
    }

    const fontAttrs = parseSavedFont(_titleFont || _headerFont || _buttonFont || _font);
    Object.assign(attributes, fontAttrs); 

    const borderAttrs = parseSavedBorder(_border);
    Object.assign(attributes, borderAttrs);

    if (!attributes.fontFamily) attributes.fontFamily = 'Poppins';
    if (!attributes.fontSize) attributes.fontSize = '12px';
    if (!attributes.fontWeight) attributes.fontWeight = 'normal';
    if (!attributes.fontStyle) attributes.fontStyle = 'normal';

    if (attributes.fontSize) attributes.fontSize = ensurePxUnit(attributes.fontSize);
    if (attributes.borderSize) attributes.borderSize = ensurePxUnit(attributes.borderSize);
    if (attributes.cornerRadius) attributes.cornerRadius = ensurePxUnit(attributes.cornerRadius);
    if (attributes.height) attributes.height = ensurePxUnit(attributes.height);
    if (attributes.width) attributes.width = ensurePxUnit(attributes.width);

    return attributes;
};




/**
 * Converts legacy XML properties (with '_' prefix, composite strings)
 * into a normalized attribute object for the React Wizard's section state.
 * @param {object} [legacyProps={}] - The raw Properties object read from the XML.
 * @returns {object} A "wizard-friendly" attributes object.
 */
export const normalizeSectionAttributesForWizard = (legacyProps = {}) => {
    const attributes = {};

    //Font Parsing
    const fontAttrs = parseSavedFont(legacyProps._titleFont, 'Poppins');
    attributes.titleFontFamily = fontAttrs.fontFamily;
    attributes.titleFontSize = removePxUnits(legacyProps._fontSize || fontAttrs.fontSize || '18');
    attributes.titleTextWeight = legacyProps._titleTextWeight || fontAttrs.fontWeight || 'normal'; 
    attributes.titleTextStyle = legacyProps._titleTextStyle || fontAttrs.fontStyle || 'normal'; 

    //Border Parsing
    const borderAttrs = parseSavedBorder(legacyProps._border);
    attributes.borderSize = removePxUnits(legacyProps._borderSize || borderAttrs.borderSize || '1'); 
    attributes.borderStyle = legacyProps._borderStyle || borderAttrs.borderStyle || 'solid';
    attributes.cornerRadius = removePxUnits(legacyProps._cornerRadius || borderAttrs.cornerRadius || '0');

    //Booleans
    attributes.hasTitle = legacyProps._hasTitle !== undefined ? util.isTrue(legacyProps._hasTitle) : true; 
    attributes.transparentBackground = legacyProps._transparentBackground !== undefined ? util.isTrue(legacyProps._transparentBackground) : false;

    //Colors 
    attributes.titleColor = formatColorForLoad(legacyProps._titleColor || '#000000');
    attributes.titleBackgroundColor = formatColorForLoad(legacyProps._titleBackgroundColor || '#FFFFFF');
    attributes.borderColor = formatColorForLoad(legacyProps._borderColor || '#C0C0C0');
    attributes.backgroundColor = formatColorForLoad(legacyProps._backgroundColor || '#ffffff');

    //Other Properties
    attributes.titleAlign = legacyProps._titleAlign || 'left';
    attributes.titleHeight = legacyProps._titleHeight || 'auto';
    attributes.titleText = legacyProps._titleText;
    attributes.titleBoldSelection = legacyProps._titleBoldSelection || '';
    attributes.titleItalicSelection = legacyProps._titleItalicSelection || '';
    attributes.backgroundImage = legacyProps._backgroundImage;
    attributes.backgroundImageAssetId = legacyProps._backgroundImageAssetId;
    attributes.backgroundImageStyle = legacyProps._backgroundImageStyle;
    attributes.backgroundImageTag = legacyProps._backgroundImageTag;
    attributes.preloadImage = legacyProps._preloadImage;

    // Remove undefined properties before returning
    Object.keys(attributes).forEach(key => {
        if (attributes[key] === undefined) {
            delete attributes[key];
        }
    });

    return attributes;
};

/**
 * Converts a wizard attribute object into the legacy format
 * expected for XML saving (with '_' prefix, composite strings).
 * @param {object} [wizardAttributes={}] - The "wizard-friendly" attributes object.
 * @returns {object} A "legacy-friendly" properties object ready for XML serialization.
 */
export const prepareSectionAttributesForLegacySave = (wizardAttributes = {}) => {
    const legacyProps = {};

    // Build Composite Strings
    const titleFontFamily = wizardAttributes.titleFontFamily || 'Poppins';
    const titleFontSize = String(wizardAttributes.titleFontSize || '18').replace('px', '');
    const titleTextWeight = wizardAttributes.titleTextWeight || 'normal';
    const titleTextStyle = wizardAttributes.titleTextStyle || 'normal';
    legacyProps._titleFont = `${titleFontFamily},${titleFontSize},${titleTextWeight},${titleTextStyle}`;
    legacyProps._font = legacyProps._titleFont; //This seems to be used by the player so adding it here

    const borderSize = String(wizardAttributes.borderSize || '1').replace('px', '');
    const borderStyle = wizardAttributes.borderStyle || 'solid';
    const cornerRadius = String(wizardAttributes.cornerRadius || '0').replace('px', '');
    legacyProps._border = `${borderSize},${borderStyle},${cornerRadius}`;

    const normalizeColorForSave = (color) => {
        if (typeof color === 'string' && color.startsWith('#')) {
            return color.substring(1);
        }
         if (typeof color === 'string' && color.startsWith('~')) {
             return color;
        }
        return color;
    };

    legacyProps._hasTitle = wizardAttributes.hasTitle !== undefined ? String(wizardAttributes.hasTitle) : 'true';
    legacyProps._transparentBackground = wizardAttributes.transparentBackground !== undefined ? String(wizardAttributes.transparentBackground) : 'false';

    legacyProps._titleColor = normalizeColorForSave(wizardAttributes.titleColor);
    legacyProps._titleBackgroundColor = normalizeColorForSave(wizardAttributes.titleBackgroundColor);
    legacyProps._borderColor = normalizeColorForSave(wizardAttributes.borderColor);
    legacyProps._backgroundColor = normalizeColorForSave(wizardAttributes.backgroundColor);

    legacyProps._titleAlign = wizardAttributes.titleAlign;
    legacyProps._titleHeight = wizardAttributes.titleHeight;
    legacyProps._titleText = wizardAttributes.titleText;
    legacyProps._titleBoldSelection = wizardAttributes.titleBoldSelection;
    legacyProps._titleItalicSelection = wizardAttributes.titleItalicSelection;
    legacyProps._backgroundImage = wizardAttributes.backgroundImage;
    legacyProps._backgroundImageAssetId = wizardAttributes.backgroundImageAssetId;
    legacyProps._backgroundImageStyle = wizardAttributes.backgroundImageStyle;
    legacyProps._backgroundImageTag = wizardAttributes.backgroundImageTag;
    legacyProps._preloadImage = wizardAttributes.preloadImage;

    legacyProps._fontSize = titleFontSize;
   
    return legacyProps;
};



/**
 * 
 * PRIVATE HELPER FUNCTIONS
 * 
 */

const removePxUnits = (value) => {
    return typeof value === 'string' ? value.replace("px","") : value;
}

const formatTime = (date, is24HourClock, hasSeconds) => {
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    let ampm = '';

    if (!is24HourClock) {
        ampm = hours >= 12 ? ' PM' : ' AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
    }

    let timeString = `${hours}:${minutes}`;
    if (hasSeconds) {
        timeString += `:${seconds}`;
    }
    return timeString + ampm;
};

const isDefault = (val) => !val || themeService.isThemeProperty(val);

const mergeMobileStyles = (styleGetter, desktopModel, mobileModel) => {
    const baseAttrs = styleGetter(desktopModel);
    const mobileAttrs = mobileModel ? styleGetter(mobileModel) : {};
    return { ...baseAttrs, ...mobileAttrs };
};

const getStylePropsGetter = (factoryKey) => {
    switch (factoryKey) {
        case 'control_text':
        case 'control_small_text':
        case 'control_large_heading':
        case 'control_medium_heading':
        case 'control_small_heading':
        case 'control_bullet_text':
        case 'control_link':
            return styleUtils.getTextStyleProps;
        case 'control_button':
        case 'control_submit_button':
        case 'control_page_button':
        case 'control_print_button':
        case 'control_play_form_button':
        case 'control_play_report_button':
            return styleUtils.getButtonStyleProps;
        case 'control_text_input':
        case 'control_numeric_input':
        case 'control_password_input':
        case 'control_multiple_text_input':
        case 'control_date_input':
        case 'control_time_input':
        case 'control_drop_down':
        case 'control_mask_text_input':
        case 'control_numeric_stepper':
        case 'control_list':
            return styleUtils.getInputStyleProps;
        case 'control_checkbox':
        case 'control_radio_button':
            return styleUtils.getSelectionControlStyleProps;
        case 'control_checkbox_group':
        case 'control_radio_button_group':
        case 'control_group':
            return styleUtils.getGroupStyleProps;
        case 'control_horizontal_separator':
        case 'control_vertical_separator':
            return styleUtils.getSeparatorsStyleProps;
        case 'control_websignature':
            return styleUtils.getWebSignatureButtonStyleProps;
        case 'control_horizontal_slider':
        case 'control_vertical_slider':
            return styleUtils.getSliderStyleProps;
        case 'control_table':
            return styleUtils.getTableStyleProps;
        case 'control_ranking':
            return styleUtils.getRankingStyleProps;
        case 'control_picture':
            return styleUtils.getImageStyleProps;
        case 'control_progress_bar':
            return styleUtils.getProgressBarStyleProps;
        case 'control_captcha':
            return styleUtils.getCaptchaStyleProps;
        case 'control_file_attachment':
            return styleUtils.getFileAttachmentStyleProps;
        default:
            console.warn(`No style getter found for factoryKey: ${factoryKey}`);
            return () => ({});
    }
};

const formatColorForLoad = (color) => {
    if (typeof color === 'string' && color.length > 0 && !color.startsWith('#') && !color.startsWith('~')) {
        return `#${color}`;
    }
    return color;
};

const parseSavedFont = (fontString, fallbackFamily = 'Poppins') => {
    if (!fontString || typeof fontString !== 'string') return {};
    const parts = fontString.split(',').map(p => p.trim());
    return {
        fontFamily: parts[0] || fallbackFamily,
        fontSize: ensurePxUnit(parts[1] || '10'),
        fontWeight: parts[2] || 'normal',
        fontStyle: parts[3] || 'normal',
    };
};

const parseSavedBorder = (borderString) => {
    if (!borderString || typeof borderString !== 'string') return {};
    const parts = borderString.split(',').map(p => p.trim());
    return {
        borderSize: ensurePxUnit(parts[0] || '0'),
        borderStyle: parts[1] || 'none',
        cornerRadius: ensurePxUnit(parts[2] || '0'),
    };
};

